/**
 * Coût des envois WhatsApp — ce que Meta facture, rendu visible.
 *
 * Jusqu'ici, envoyer un modèle hors fenêtre de service coûtait de l'argent sans
 * que rien ne l'indique nulle part : ni à l'écran, ni dans un rapport. On
 * découvrait le montant sur la facture Meta, un mois plus tard.
 *
 * COMMENT META FACTURE (et pourquoi ce compteur est une ESTIMATION) :
 * la facturation porte sur la CONVERSATION de 24 h ouverte par un modèle, pas
 * sur chaque message. Envoyer trois modèles au même praticien dans la même
 * journée ouvre UNE conversation facturée, pas trois. On compte donc les
 * conversations distinctes ayant reçu au moins un modèle dans le mois — l'unité
 * la plus proche de la réalité — et on l'annonce comme une estimation, jamais
 * comme un montant dû.
 */
import { z } from "zod";

/**
 * Tarif « utilitaire » France, ordre de grandeur 2026. Configurable par
 * `WA_TARIF_UTILITY_EUR` : un tarif codé en dur devient faux sans prévenir.
 */
export const TARIF_UTILITY_DEFAUT = 0.0357;

export const CoutMoisSchema = z.object({
  /** AAAA-MM */
  mois: z.string().regex(/^\d{4}-\d{2}$/),
  /** modèles réellement envoyés — utile pour comprendre l'usage */
  modelesEnvoyes: z.number().int().min(0),
  /** conversations distinctes ayant reçu un modèle : l'unité facturée */
  conversationsFacturables: z.number().int().min(0),
  estimationEuros: z.number().min(0),
});
export type CoutMois = z.infer<typeof CoutMoisSchema>;

export const CoutWhatsappSchema = z.object({
  tarifUnitaire: z.number().min(0),
  /** du mois le plus récent au plus ancien */
  mois: z.array(CoutMoisSchema),
  /** depuis quand l'historique existe : avant, on ne sait pas, et on le dit */
  depuis: z.string().nullable(),
});
export type CoutWhatsapp = z.infer<typeof CoutWhatsappSchema>;

/** Arrondi au centime — un coût s'affiche en euros, pas en flottant brut. */
export function euros(montant: number): number {
  return Math.round(montant * 100) / 100;
}

export function estimerMois(conversationsFacturables: number, tarif: number): number {
  return euros(conversationsFacturables * tarif);
}

/** « 2026-08 » → « août 2026 » */
const MOIS_FR = [
  "janvier", "février", "mars", "avril", "mai", "juin",
  "juillet", "août", "septembre", "octobre", "novembre", "décembre",
];
export function moisLisible(mois: string): string {
  const [a, m] = mois.split("-");
  const i = Number(m) - 1;
  return `${MOIS_FR[i] ?? m} ${a ?? ""}`.trim();
}
